import React from 'react';
import { ArrowUpDown, Building2, MapPin } from 'lucide-react';

interface AccessibilityInputProps {
  value: {
    floor: number;
    elevator: boolean;
    narrowStreet: boolean;
  };
  onChange: (accessibility: {
    floor: number;
    elevator: boolean;
    narrowStreet: boolean;
  }) => void;
}

const defaultValue = {
  floor: 0,
  elevator: false,
  narrowStreet: false
};

export const AccessibilityInput: React.FC<AccessibilityInputProps> = ({ 
  value = defaultValue, 
  onChange 
}) => {
  // Ensure we have default values if value is undefined
  const safeValue = {
    floor: value?.floor ?? 0,
    elevator: value?.elevator ?? false,
    narrowStreet: value?.narrowStreet ?? false
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Étage
        </label>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            value={safeValue.floor}
            onChange={(e) => onChange({ ...safeValue, floor: parseInt(e.target.value) || 0 })}
            min="0"
            className="input w-32"
            placeholder="0"
          />
          <span className="text-sm text-neutral-500">(0 = rez-de-chaussée)</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <AccessibilityCard
          icon={<Building2 className="h-6 w-6" />}
          label="Ascenseur"
          value={safeValue.elevator}
          onChange={(elevator) => onChange({ ...safeValue, elevator })}
        />
        <AccessibilityCard
          icon={<MapPin className="h-6 w-6" />}
          label="Rue étroite"
          value={safeValue.narrowStreet}
          onChange={(narrowStreet) => onChange({ ...safeValue, narrowStreet })}
        />
      </div>
    </div>
  );
};

interface AccessibilityCardProps {
  icon: React.ReactNode;
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

const AccessibilityCard: React.FC<AccessibilityCardProps> = ({
  icon,
  label,
  value,
  onChange
}) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`
      card flex items-center p-4 transition-all
      ${value ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-neutral-50'}
    `}
  >
    <div className={`mr-3 ${value ? 'text-primary' : 'text-neutral-600'}`}>
      {icon}
    </div>
    <span className={`font-medium ${value ? 'text-primary' : 'text-neutral-700'}`}>
      {label}
    </span>
  </button>
);